import { Directive, input } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import {
  ConfigurableGoalInterface,
  FunctionApiService,
  GoalInterface,
  GoalKeyInterface,
  GoalType,
  KeyValuePair,
  NamespaceInterface,
  PromptModuleApiService,
} from '@vendasta/ai-assistants';
import { firstValueFrom, map, Observable, shareReplay } from 'rxjs';
import { debounceTime, distinctUntilChanged, startWith } from 'rxjs/operators';
import { DEFAULT_LEAD_CAPTURE_GOAL_ID } from '../ai-assistant.constants';
import { AiToolFormRegistryService } from '../services/ai-tool-form-registry.service';
import { extractVariablesFromMultipleSources } from '../utils/variable-detection.util';
import { extractTextForVariableDetection } from '../utils/variable-extraction.util';
import { AiConstraintsForm } from './ai-constraints.form';
import { AiPromptModuleFormArray } from './ai-prompt-module.form';
import { AiToolForm, AiToolFormArray } from './ai-tools.form';
import { FormArrayWithDirtyTracking } from './forms';

export type CapabilityConfigurationFormControl = FormControl<KeyValuePair[] | null>;

export function getKeyValuePairFromCapabilityFormControl(
  formControl: CapabilityConfigurationFormControl,
  key: string,
): KeyValuePair {
  const formValue = formControl.value || [];
  return formValue?.find((pair) => pair.key === key) || new KeyValuePair({ key: key, value: '' });
}

export function setKeyValuePairFromCapabilityFormControl(
  formControl: CapabilityConfigurationFormControl,
  newPair: KeyValuePair,
): void {
  const formValue = formControl.value || [];
  let pairFound = false;
  formValue.map((pair) => {
    if (pair.key === newPair.key) {
      pair.value = newPair.value;
      pairFound = true;
    }
  });
  if (!pairFound) {
    formValue.push(
      new KeyValuePair({
        key: newPair.key,
        value: newPair.value,
      }),
    );
  }
  formControl.setValue(formValue);
}

// Capabilities
export type AiCapabilityFormControls = {
  goalId: FormControl<string | null>;
  // TODO: Can this be non-nullable?
  namespace: FormControl<NamespaceInterface | null>;
  managed: FormControl<boolean>;
  name: FormControl<string | null>;
  description: FormControl<string | null>;
  type: FormControl<GoalType | null>;
  promptModules: AiPromptModuleFormArray;
  tools: AiToolFormArray;
  configuration: CapabilityConfigurationFormControl;
  constraints: AiConstraintsForm;
  overrideOf: FormControl<GoalKeyInterface | null>;
};

export class AiCapabilityForm extends FormGroup<AiCapabilityFormControls> {
  isHidden = false;

  // Observable of available variables (variables found in text)
  readonly availableVariables$: Observable<string[]>;

  constructor(
    promptModuleApiService: PromptModuleApiService,
    goal?: ConfigurableGoalInterface,
    promptModuleContents?: Record<string, string | undefined>,
    toolFormRegistry?: AiToolFormRegistryService,
  ) {
    const capability = goal?.goal ?? null;
    const keyValuePairs = goal?.configuration
      ?.map((v): KeyValuePair => {
        if (v instanceof KeyValuePair) {
          return v;
        }
        return new KeyValuePair(v);
      })
      ?.filter((v) => Boolean(v.key && v.value));

    super({
      goalId: new FormControl({ value: capability?.id ?? null, disabled: true }),
      namespace: new FormControl({ value: capability?.namespace ?? null, disabled: true }),
      managed: new FormControl({ value: capability?.managed ?? false, disabled: true }, { nonNullable: true }),
      name: new FormControl(capability?.name ?? null, Validators.required),
      description: new FormControl(capability?.description ?? null),
      type: new FormControl({ value: capability?.type ?? GoalType.GOAL_TYPE_CUSTOM, disabled: true }),
      promptModules: new AiPromptModuleFormArray({
        promptModules: capability?.promptModules || [],
        contents: promptModuleContents || {},
        promptModuleApiService,
      }),
      tools: new AiToolFormArray(capability?.functions || [], toolFormRegistry),
      configuration: new FormControl<KeyValuePair[] | null>(keyValuePairs ?? null),
      constraints: new AiConstraintsForm(capability?.constraints || []),
      overrideOf: new FormControl<GoalKeyInterface | null>(capability?.overrideOf ?? null),
    });

    this.controls.configuration.enable();

    const isPersonalityType = this.controls.type.getRawValue() === GoalType.GOAL_TYPE_PERSONALITY;
    const hasViewDenyConstraint = this.controls.constraints.controls.isHidden.getRawValue();
    this.isHidden = isPersonalityType || hasViewDenyConstraint;

    // Build text sources observable
    const textSources$ = this.valueChanges.pipe(
      debounceTime(800),
      startWith(null),
      map(() => extractTextForVariableDetection(this.controls.promptModules.controls, this.controls.tools.controls)),
      distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );

    // Build available variables observable from text sources
    this.availableVariables$ = textSources$.pipe(
      map((textSources) => extractVariablesFromMultipleSources(textSources)),
      distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );
  }

  /**
   * Static factory method to create AiCapabilityForm with full function details
   */
  static async createWithFunctionDetails(
    promptModuleApiService: PromptModuleApiService,
    goal?: ConfigurableGoalInterface,
    promptModuleContents?: Record<string, string | undefined>,
    toolFormRegistry?: AiToolFormRegistryService,
    functionApiService?: FunctionApiService,
  ): Promise<AiCapabilityForm> {
    let enrichedGoal = goal;

    // Fetch function details if we have a functionApiService and goal with functions
    if (functionApiService && goal?.goal?.functions?.length && toolFormRegistry) {
      const enrichedGoalData = await AiCapabilityForm.fetchFunctionDetailsForGoal(
        goal.goal,
        functionApiService,
        toolFormRegistry,
      );
      enrichedGoal = {
        ...goal,
        goal: enrichedGoalData,
      };
    }

    return new AiCapabilityForm(promptModuleApiService, enrichedGoal, promptModuleContents, toolFormRegistry);
  }

  /**
   * Fetches full function details for all functions in a goal, using tool registry when available
   */
  private static async fetchFunctionDetailsForGoal(
    goal: GoalInterface,
    functionApiService: FunctionApiService,
    toolFormRegistry: AiToolFormRegistryService,
  ): Promise<GoalInterface> {
    if (!goal.functions?.length) {
      return goal;
    }

    // Get full function details for each function in the goal
    const functionPromises = goal.functions.map(async (functionKey) => {
      if (!functionKey.id || !functionKey.namespace) {
        return null;
      }

      // Check if we already have this function in the tool registry
      if (toolFormRegistry.hasRegisteredForm(functionKey.id)) {
        // Extract function details from the existing form
        const existingForm = toolFormRegistry.getOrRegister(functionKey.id, () => {
          // This shouldn't be called since we checked hasRegisteredForm first,
          // but provide a fallback just in case
          return new AiToolForm();
        });

        // Convert the form back to function interface
        const existingFunction = existingForm.toFunction(functionKey.namespace);
        return existingFunction;
      }

      // Function not in registry, fetch it from API
      const response = await firstValueFrom(
        functionApiService.get({
          id: functionKey.id,
          namespace: functionKey.namespace,
        }),
      );

      const fetchedFunction = response?.function;
      if (fetchedFunction) {
        // Add the fetched function to the registry by creating a tool form
        toolFormRegistry.getOrRegister(functionKey.id, () => {
          return new AiToolForm({ func: fetchedFunction });
        });
      }

      return fetchedFunction || null;
    });

    const functionDetails = await Promise.all(functionPromises);
    const validFunctions = functionDetails.filter((fn) => fn !== null);

    // Return goal with full function details instead of just references
    return {
      ...goal,
      functions: validFunctions,
    };
  }

  toGoalKey(): GoalKeyInterface | null {
    const id = this.controls.goalId.getRawValue();
    const namespace = this.controls.namespace.getRawValue();
    if (!id || !namespace) return null;
    return { id, namespace };
  }

  // TODO: We need to ensure this is called after...
  //  toGoal is called + the new goal is created + the goalID is set on the form
  // in the modified global/managed case
  toConfigurableGoal(fallbackNamespace: NamespaceInterface | undefined): ConfigurableGoalInterface {
    return {
      goal: {
        id: this.controls.goalId.getRawValue() ?? undefined,
        namespace: this.controls.namespace.getRawValue() ?? fallbackNamespace,
      },
      configuration: this.controls.configuration.getRawValue() ?? undefined,
    };
  }

  // toGoal creates a goal that can be used in an create/update/upsert goal API call
  // - If the form contains a global goal, Then a new locally namespaced goal
  //   will be returned with a reference to the goal it is based on.
  // - If the form contains a managed goal, Then a new locally namespaced goal
  //   that is not managed will be returned with a reference to the goal it is based on.
  // In both of the cases above, the form values will be modified
  toGoal(localNamespace: NamespaceInterface): GoalInterface {
    if (this.needsGoalForking()) {
      const originalGoalId = this.controls.goalId.getRawValue();
      const originalGoalNamespace = this.controls.namespace.getRawValue();
      this.controls.overrideOf.setValue({
        id: originalGoalId ?? undefined,
        namespace: originalGoalNamespace ?? undefined,
      });
      this.controls.goalId.setValue(null);
      this.controls.namespace.setValue(localNamespace);
      this.controls.managed.setValue(false);
    }

    return {
      id: this.controls.goalId.getRawValue() ?? undefined,
      name: this.controls.name.getRawValue() ?? undefined,
      namespace: this.controls.namespace.getRawValue() ?? localNamespace,
      description: this.controls.description.getRawValue() ?? undefined,
      type: this.controls.type.getRawValue() ?? undefined,
      functions: this.controls.tools.toFunctionKeys(localNamespace),
      promptModules: this.controls.promptModules.getPromptModuleKeys(localNamespace),
      constraints: this.controls.constraints.toConstraints(),
      overrideOf: this.controls.overrideOf.getRawValue() ?? undefined,
    };
  }

  isOverride(): boolean {
    return Boolean(this.controls.overrideOf.getRawValue());
  }

  isGlobalOrManaged(): boolean {
    const ns = this.controls.namespace.getRawValue();
    return Boolean(ns?.globalNamespace) || this.controls.managed.getRawValue();
  }

  isLeadCaptureCapability(): boolean {
    return this.controls.goalId.getRawValue() === DEFAULT_LEAD_CAPTURE_GOAL_ID;
  }

  hasOnlyConfigurationChanges(): boolean {
    // If configuration is dirty but the goal definition fields are not dirty
    const configurationDirty = this.controls.configuration.dirty;
    const goalDefinitionDirty = Object.entries(this.controls)
      .filter(([key]) => key !== 'configuration')
      .some(([, control]) => control.dirty);

    return configurationDirty && !goalDefinitionDirty;
  }

  needsGoalForking(): boolean {
    // Only fork the goal if it's global/managed AND has goal definition changes
    // If only configuration changes, we don't need to fork
    return this.isGlobalOrManaged() && !this.hasOnlyConfigurationChanges();
  }

  patchCapability(form: AiCapabilityForm, options?: { onlySelf?: boolean; emitEvent?: boolean }): void {
    // Do what we were doing before (patchValue with getRawValue) but with proper tools handling
    const replacementValues = form.getRawValue();
    this.patchValue(replacementValues, options);

    // Manually handle the tools FormArray by clearing and repopulating (the missing piece)
    this.controls.tools.clear();
    form.controls.tools.controls.forEach((toolForm) => {
      this.controls.tools.push(toolForm);
    });

    // Manually handle the promptModules FormArray by clearing and repopulating
    this.controls.promptModules.clear();
    form.controls.promptModules.controls.forEach((promptModuleForm) => {
      this.controls.promptModules.push(promptModuleForm);
    });

    // Manually handle the constraints assignmentConstraints FormArray
    this.controls.constraints.controls.assignmentConstraints.clear();
    form.controls.constraints.controls.assignmentConstraints.controls.forEach((constraintForm) => {
      this.controls.constraints.controls.assignmentConstraints.push(constraintForm);
    });
  }
}

export class AiCapabilityFormArray extends FormArrayWithDirtyTracking<AiCapabilityForm> {
  constructor(
    promptModuleApiService: PromptModuleApiService,
    capabilities: ConfigurableGoalInterface[],
    promptModuleContents: Record<string, string>,
    toolFormRegistry?: AiToolFormRegistryService,
  ) {
    super(
      capabilities.map(
        (capability) =>
          new AiCapabilityForm(promptModuleApiService, capability, promptModuleContents, toolFormRegistry),
      ),
    );
  }

  /**
   * Static factory method to create AiCapabilityFormArray with full function details
   */
  static async createWithFunctionDetails(
    promptModuleApiService: PromptModuleApiService,
    capabilities: ConfigurableGoalInterface[],
    promptModuleContents: Record<string, string>,
    toolFormRegistry?: AiToolFormRegistryService,
    functionApiService?: FunctionApiService,
  ): Promise<AiCapabilityFormArray> {
    // Create all capability forms with function details concurrently
    const capabilityFormPromises = capabilities.map((capability) =>
      AiCapabilityForm.createWithFunctionDetails(
        promptModuleApiService,
        capability,
        promptModuleContents,
        toolFormRegistry,
        functionApiService,
      ),
    );

    const capabilityForms = await Promise.all(capabilityFormPromises);

    // Create the form array with the enriched forms
    const formArray = new AiCapabilityFormArray(promptModuleApiService, [], promptModuleContents, toolFormRegistry);

    // Clear the initial empty array and add the enriched forms
    formArray.clear();
    capabilityForms.forEach((form) => formArray.push(form));

    return formArray;
  }

  toConfigurableGoals(localNamespace: NamespaceInterface): ConfigurableGoalInterface[] {
    return this.controls
      .filter((goal) => {
        // This prevents us from calling upsertAssistant with the empty personality goal we add when no personality goal exists on the assistant
        return !(
          goal.controls.type.getRawValue() === GoalType.GOAL_TYPE_PERSONALITY && !goal.controls.goalId.getRawValue()
        );
      })
      .map((capabilityForm) => capabilityForm.toConfigurableGoal(localNamespace));
  }
}

@Directive()
export abstract class CustomCapabilityConfigurationForm {
  configurationForm = input.required<CapabilityConfigurationFormControl>();
  goalId = input<string>();
  disableConfigurableGoalFields = input<boolean>(false);
}
